/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

// AIDEV-NOTE: Columns and members are readonly at source rather than wrapped in
// DeepReadonly at each call site. Nothing mutates a column in place - tables are
// always rebuilt by mapping - so this costs nothing at runtime and removes the
// readonly-erasing casts these types used to need. The Record itself stays mutable
// so builders can keep assigning whole columns by key.
export type NumericTable = Record<string, readonly number[]>

export type UiTable = Record<string, readonly (string | number)[]>

/**
 * Prepared data table with properly typed columns for calculation
 * Column names match the server-side implementation
 */
export interface InitialScrsTable {
  readonly initial_scr: readonly number[]
  readonly year: readonly number[]
  readonly month: readonly number[]
  readonly caey_ern_1M_lag: readonly number[]
  readonly caey_shiller_1M_lag: readonly number[]
  readonly real_equity_drawdown_1M_lag: readonly number[]
  readonly c1: readonly number[]
  readonly sum_of_ct: readonly number[]
  readonly sum_of_ctpt: readonly number[]
}

export interface Cape {
  readonly ern: boolean
  readonly minValue?: number
  readonly maxValue?: number
}

export interface Cohort {
  readonly cape?: Cape
  readonly firstStartingYear?: number
  readonly lastStartingYear?: number
  readonly minAbsDrawdown?: number
  readonly maxAbsDrawdown?: number
  readonly description?: string
}

export const FAILURE_RATES = [0.0, 0.01, 0.02, 0.05, 0.1, 0.25, 0.5]

/**
 * Data structure for final values distribution visualization
 */
export interface FinalValuesDistribution {
  readonly values: readonly number[] // Dollar values for display
  readonly proportions: readonly number[] // Proportion values (for internal use)
  readonly statistics: {
    readonly mean: number
    readonly median: number
    readonly min: number
    readonly max: number
    // Proportion statistics for potential future use
    readonly proportions: {
      readonly mean: number
      readonly median: number
      readonly min: number
      readonly max: number
    }
  }
  readonly histogramData: {
    readonly bins: readonly number[] // Bin centers or starting points (in dollars)
    readonly counts: readonly number[] // Count or probability for each bin
    readonly rawCounts: readonly number[] // Raw count of scenarios in each bin
    readonly binRanges: readonly { readonly min: number; readonly max: number }[] // Min/max boundaries for each bin
  }
  readonly annualConsumptionRateUsed: number
  readonly initialPortfolioDollars: number
}
